import { Controller } from "@hotwired/stimulus";

const meaningfulLines = (text) =>
  text
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

const lineDiff = (before, after) => {
  const lengths = before.map(() => new Array(after.length + 1).fill(0));
  lengths.push(new Array(after.length + 1).fill(0));

  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      lengths[i][j] =
        before[i] === after[j] ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
    }
  }

  const changes = [];
  let i = 0;
  let j = 0;
  while (i < before.length || j < after.length) {
    if (i < before.length && j < after.length && before[i] === after[j]) {
      i++;
      j++;
    } else if (i < before.length && (j === after.length || lengths[i + 1][j] >= lengths[i][j + 1])) {
      changes.push(`- ${before[i++]}`);
    } else {
      changes.push(`+ ${after[j++]}`);
    }
  }
  return changes;
};

export default class extends Controller {
  static targets = ["frame", "subject", "result", "output", "status"];
  static values = { instruction: String, labels: Object };

  connect() {
    this.originals = {};
    this.subjectTargets.forEach((subject) => {
      this.originals[`subject-${subject.dataset.language}`] = subject.textContent.trim();
    });
    this.frameTargets.forEach((frame) => {
      if (frame.contentDocument?.readyState === "complete") this.prepare(frame);
      frame.addEventListener("load", () => this.prepare(frame));
    });
  }

  prepare(frame) {
    const document = frame.contentDocument;
    if (!document?.body || document.location.href === "about:blank") return;

    this.originals[`body-${frame.dataset.language}`] ??= document.body.innerText;
    document.designMode = "on";
    frame.style.height = `${document.documentElement.scrollHeight}px`;
  }

  async copy() {
    const changes = this.changes();
    if (changes.length === 0) {
      this.resultTarget.hidden = true;
      this.statusTarget.textContent = this.labelsValue.noChanges;
      return;
    }

    const text = [this.instructionValue, `${this.labelsValue.changes}:`, ...changes].join("\n");
    this.outputTarget.value = text;
    this.resultTarget.hidden = false;
    await this.writeToClipboard(text);
    this.statusTarget.textContent = this.labelsValue.copied;
  }

  changes() {
    return this.frameTargets.flatMap((frame) => {
      const language = frame.dataset.language;
      const languageName = this.labelsValue.languages[language];
      const subject = this.subjectTargets.find((target) => target.dataset.language === language);
      const subjectBefore = this.originals[`subject-${language}`];
      const subjectAfter = subject.textContent.trim();
      const bodyChanges = lineDiff(
        meaningfulLines(this.originals[`body-${language}`] ?? ""),
        meaningfulLines(frame.contentDocument?.body?.innerText ?? ""),
      );

      const lines = [];
      if (subjectBefore !== subjectAfter) {
        lines.push(`${this.labelsValue.subject} (${languageName}):`, `- ${subjectBefore}`, `+ ${subjectAfter}`);
      }
      if (bodyChanges.length > 0) {
        lines.push(`${this.labelsValue.body} (${languageName}):`, ...bodyChanges);
      }
      return lines;
    });
  }

  async writeToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      this.outputTarget.select();
      document.execCommand("copy");
    }
  }
}
